import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { HttpParams } from '@angular/common/http'
import { MediaHubApiService } from './media-hub-api.service'
import { AssetType, MediaAsset, MediaAssetStatus } from '../models/media-asset.model'


export interface CreateMediaAssetUrlRequest {
  name: string
  assetType: AssetType
  url: string
  description?: string
  tags?: string[]
  metadata?: Record<string, any>
}

export interface CreateMediaAssetDirectRequest {
  name: string
  assetType: AssetType
  contentType: string
  publisherId?: string
  description?: string
  tags?: string[]
  metadata?: Record<string, any>
}

export interface UpdateMediaAssetRequest {
  name?: string
  description?: string
  tags?: string[]
  metadata?: Record<string, any>
}

export interface MediaAssetQueryParams {
  assetType?: AssetType
  status?: MediaAssetStatus
  publisherId?: string
}

export interface MediaAssetResponse {
  asset: MediaAsset
  uploadUrl?: string
  message?: string
}

export interface MediaAssetsListResponse {
  assets: MediaAsset[]
  count: number
}

export interface GetMediaAssetsByIdsRequest {
  mediaAssetIds: string[]
}

export interface TrimMediaAssetRequest {
  startTime: number
  endTime: number
  replaceOriginal: boolean
  name: string
  playlistId: string
}

export interface VerticalMediaAssetRequest {
  /** Force the rendering; by default the plan decides from how often the record places a subject. */
  mode?: 'crop' | 'blur'
  /** People to follow first, by name. */
  subjects?: string[]
  /** A playlist to add the vertical clip to, after the original. */
  playlistId?: string
  /** The chat delivery the original belongs to: the vertical clip is appended to it. */
  promptId?: string
}

export interface VerticalMediaAssetResponse {
  success: boolean
  message: string
  mediaAsset: {
    id: string
    name: string
    clipUrl: string | null
    thumbnailUrl: string | null
    clipData: { id: number; startTime: string; endTime: string; description?: string; reason?: string; reframe: { aspect: '9:16'; mode: 'crop' | 'blur' } }
  }
  plan: { mode: 'crop' | 'blur'; coverage: number; subject: string | null; notes: string[] }
}

export interface RecutMediaAssetRequest {
  startSeconds: number
  endSeconds: number
  promptId?: string
  /** the chat the cut was made in, when there is no delivery to join: the clip becomes a delivery there */
  chatId?: string
  name?: string
  /** true: a 9:16 cut; false: the original frame; left out: what the clip already was */
  vertical?: boolean
}

export interface RecutMediaAssetResponse {
  success: boolean
  message: string
  mediaAsset: { id: string; name: string; clipUrl: string | null; thumbnailUrl: string | null; clipData: { id: number; startTime: string; endTime: string; reframe?: { aspect: '9:16'; mode: 'crop' | 'blur' } } }
  /** the delivery made for the clip in `chatId`'s chat */
  promptId?: string
  /** what the 9:16 plan did, or that the cutter could not render it */
  notes?: string[]
}

export interface TrimMediaAssetResponse {
  success: boolean
  message: string
  mediaAsset: {
    id: string
    name: string
    clipData: {
      startTime: string
      endTime: string
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class MediaAssetApiService {
  private readonly endpoint = '/media-assets'
  private api = inject(MediaHubApiService)

  createMediaAssetFromUrl(request: CreateMediaAssetUrlRequest): Promise<MediaAssetResponse> {
    return this.api.postBase<CreateMediaAssetUrlRequest, MediaAssetResponse>(this.endpoint, request)
  }

  createMediaAssetFromUrl$(request: CreateMediaAssetUrlRequest): Observable<MediaAssetResponse> {
    return this.api.postBaseRx<CreateMediaAssetUrlRequest, MediaAssetResponse>(this.endpoint, request)
  }

  createMediaAssetDirect(request: CreateMediaAssetDirectRequest): Promise<MediaAssetResponse> {
    return this.api.postBase<CreateMediaAssetDirectRequest, MediaAssetResponse>(this.endpoint, request)
  }

  createMediaAssetDirect$(request: CreateMediaAssetDirectRequest): Observable<MediaAssetResponse> {
    return this.api.postBaseRx<CreateMediaAssetDirectRequest, MediaAssetResponse>(this.endpoint, request)
  }

  confirmUpload(assetId: string): Promise<MediaAssetResponse> {
    return this.api.postBase<void, MediaAssetResponse>(`${this.endpoint}/${assetId}/confirm-upload`)
  }

  confirmUpload$(assetId: string): Observable<MediaAssetResponse> {
    return this.api.postBaseRx<void, MediaAssetResponse>(`${this.endpoint}/${assetId}/confirm-upload`)
  }

  getAllMediaAssets(filters?: MediaAssetQueryParams): Promise<MediaAssetsListResponse> {
    const params = this.buildQueryParams(filters)
    return this.api.getBase<MediaAssetsListResponse>(this.endpoint, params)
  }

  getAllMediaAssets$(filters?: MediaAssetQueryParams): Observable<MediaAssetsListResponse> {
    const params = this.buildQueryParams(filters)
    return this.api.getBaseRx<MediaAssetsListResponse>(this.endpoint, params)
  }

  getMediaAssetById(id: string): Promise<MediaAssetResponse> {
    return this.api.getBase<MediaAssetResponse>(`${this.endpoint}/${id}`)
  }

  getMediaAssetById$(id: string): Observable<MediaAssetResponse> {
    return this.api.getBaseRx<MediaAssetResponse>(`${this.endpoint}/${id}`)
  }

  getMediaAssetsByIds(ids: string[]): Promise<MediaAssetsListResponse> {
    const request: GetMediaAssetsByIdsRequest = { mediaAssetIds: ids }
    return this.api.postBase<GetMediaAssetsByIdsRequest, MediaAssetsListResponse>(`${this.endpoint}/list`, request)
  }

  getMediaAssetsByIds$(ids: string[]): Observable<MediaAssetsListResponse> {
    const request: GetMediaAssetsByIdsRequest = { mediaAssetIds: ids }
    return this.api.postBaseRx<GetMediaAssetsByIdsRequest, MediaAssetsListResponse>(`${this.endpoint}/list`, request)
  }

  updateMediaAsset(id: string, request: UpdateMediaAssetRequest): Promise<MediaAssetResponse> {
    return this.api.putBase<UpdateMediaAssetRequest, MediaAssetResponse>(`${this.endpoint}/${id}`, request)
  }

  updateMediaAsset$(id: string, request: UpdateMediaAssetRequest): Observable<MediaAssetResponse> {
    return this.api.putBaseRx<UpdateMediaAssetRequest, MediaAssetResponse>(`${this.endpoint}/${id}`, request)
  }

  deleteMediaAsset(id: string): Promise<{ message: string }> {
    return this.api.deleteBase<{ message: string }>(`${this.endpoint}/${id}`)
  }

  deleteMediaAsset$(id: string): Observable<{ message: string }> {
    return this.api.deleteBaseRx<{ message: string }>(`${this.endpoint}/${id}`)
  }

  trimMediaAsset(id: string, request: TrimMediaAssetRequest): Promise<TrimMediaAssetResponse> {
    return this.api.postBase<TrimMediaAssetRequest, TrimMediaAssetResponse>(`${this.endpoint}/${id}/trim`, request)
  }

  trimMediaAsset$(id: string, request: TrimMediaAssetRequest): Observable<TrimMediaAssetResponse> {
    return this.api.postBaseRx<TrimMediaAssetRequest, TrimMediaAssetResponse>(`${this.endpoint}/${id}/trim`, request)
  }

  /** A clip cut again from its source (or a video cut directly) at the user's own seconds — the editor. */
  recutMediaAsset$(id: string, request: RecutMediaAssetRequest): Observable<RecutMediaAssetResponse> {
    return this.api.postBaseRx<RecutMediaAssetRequest, RecutMediaAssetResponse>(`${this.endpoint}/${id}/recut`, request)
  }

  /** A 9:16 rendering of a clip, cut again from its source with the picture planned off the source's record. */
  verticalMediaAsset$(id: string, request: VerticalMediaAssetRequest = {}): Observable<VerticalMediaAssetResponse> {
    return this.api.postBaseRx<VerticalMediaAssetRequest, VerticalMediaAssetResponse>(`${this.endpoint}/${id}/vertical`, request)
  }

  downloadVisualDescription(assetId: string): Promise<{ body: Blob | null; fileName: string | null }> {
    return this.api.getBaseBlob(`${this.endpoint}/${assetId}/vds3/record/download`)
  }

  downloadTranscription(assetId: string): Promise<{ body: Blob | null; fileName: string | null }> {
    return this.api.getBaseBlob(`${this.endpoint}/${assetId}/transcription-tasks/download`)
  }

  /** One analysis document of an asset (the v1 schema): 'sports', 'people', … A 404 means the asset has none. */
  getAnalysisDoc<T>(assetId: string, doc: string): Promise<{ doc: string; data: T; stored: boolean }> {
    return this.api.getBase(`${this.endpoint}/${assetId}/analysis/${doc}`)
  }

  private buildQueryParams(filters?: MediaAssetQueryParams): HttpParams | undefined {
    if (!filters) return undefined

    let params = new HttpParams()

    if (filters.assetType) {
      params = params.set('assetType', filters.assetType)
    }

    if (filters.status) {
      params = params.set('status', filters.status)
    }

    if (filters.publisherId) {
      params = params.set('publisherId', filters.publisherId)
    }

    return params
  }
}
